import { getCombatMovement } from "../utils/derivedStats.js";
import { EntityStatus } from "../entity/types.js";
/**
 * Battlefield wraps a CombatStateDTO and provides convenient APIs for accessing combat map functions
 */
export class Battlefield {
    constructor(combatData, def) {
        this.combatData = combatData;
        this.def = def;
    }
    get width() {
        return this.def.width;
    }
    get height() {
        return this.def.height;
    }
    distanceBetween(a, b) {
        // Manhattan distance
        return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
    }
    isValidMoveTarget(entity, from, to) {
        // Check bounds
        if (to.x < 0 || to.y < 0 || to.x >= this.width || to.y >= this.height) {
            return false;
        }
        // Check blocked tiles
        if (this.isBlockedMove(to)) {
            return false;
        }
        const movement = getCombatMovement(entity);
        // Quick reject: Manhattan distance already exceeds movement (path can only be equal or longer)
        const distance = this.distanceBetween(from, to);
        if (distance === 0 || distance > movement)
            return false;
        // Check if tile is occupied
        for (const e of this.combatData.entityStates) {
            if (e.status === EntityStatus.DEAD)
                continue;
            if (e.position.x === to.x && e.position.y === to.y) {
                return false;
            }
        }
        // Verify a valid path exists that stays within movement range and avoids blocked/occupied tiles
        const path = this.findPath(from, to, { ignoreEntityId: entity.id });
        if (!path || path.length - 1 > movement)
            return false;
        return true;
    }
    isBlockedMove(pos) {
        return this.def.blockedMove.some(b => b.x === pos.x && b.y === pos.y);
    }
    isBlockedLos(pos) {
        return this.def.blockedLos.some(b => b.x === pos.x && b.y === pos.y);
    }
    hasLineOfSight(from, to, ignoreEntityIds = [], actingGroupId) {
        const cells = this.superCoverLine(from, to);
        for (const cell of cells) {
            if ((cell.x === from.x && cell.y === from.y) || (cell.x === to.x && cell.y === to.y))
                continue;
            if (this.isBlockedLos(cell))
                return false;
            if (this.combatData.entityStates.some(e => e.status !== EntityStatus.DEAD &&
                !ignoreEntityIds.includes(e.entityId) &&
                (actingGroupId === undefined || e.groupId !== actingGroupId) &&
                e.position.x === cell.x && e.position.y === cell.y))
                return false;
        }
        return true;
    }
    superCoverLine(from, to) {
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const nx = Math.abs(dx);
        const ny = Math.abs(dy);
        const signX = dx > 0 ? 1 : -1;
        const signY = dy > 0 ? 1 : -1;
        const cells = [{ x: from.x, y: from.y }];
        let x = from.x;
        let y = from.y;
        let ix = 0;
        let iy = 0;
        while (ix < nx || iy < ny) {
            if ((0.5 + ix) / nx < (0.5 + iy) / ny) {
                x += signX;
                ix++;
            }
            else {
                y += signY;
                iy++;
            }
            cells.push({ x, y });
        }
        return cells;
    }
    getValidMovePositions(entity, position) {
        const movement = getCombatMovement(entity);
        const positions = [];
        for (let dx = -movement; dx <= movement; dx++) {
            for (let dy = -movement; dy <= movement; dy++) {
                if (dx == 0 && dy == 0)
                    continue;
                if (this.distanceBetween(position, { x: position.x + dx, y: position.y + dy }) <= movement) {
                    const pos = { x: position.x + dx, y: position.y + dy };
                    if (this.isValidMoveTarget(entity, position, pos)) {
                        positions.push(pos);
                    }
                }
            }
        }
        return positions;
    }
    findPath(from, to, options) {
        if (!this.isInBounds(from) || !this.isInBounds(to))
            return null;
        if (from.x === to.x && from.y === to.y)
            return [from];
        if (this.isBlockedMove(to))
            return null;
        if (this.isOccupied(to, options?.ignoreEntityId) && !options?.allowOccupiedGoal)
            return null;
        const startKey = this.keyFor(from);
        const goalKey = this.keyFor(to);
        const openSet = new Set([startKey]);
        const cameFrom = new Map();
        const gScore = new Map([[startKey, 0]]);
        const fScore = new Map([[startKey, this.distanceBetween(from, to)]]);
        while (openSet.size > 0) {
            let currentKey = null;
            let currentScore = Infinity;
            for (const key of openSet) {
                const score = fScore.get(key) ?? Infinity;
                if (score < currentScore) {
                    currentScore = score;
                    currentKey = key;
                }
            }
            if (!currentKey)
                break;
            if (currentKey === goalKey) {
                return this.reconstructPath(cameFrom, currentKey);
            }
            openSet.delete(currentKey);
            const currentPos = this.posFromKey(currentKey);
            for (const neighbor of this.getNeighbors(currentPos)) {
                const neighborKey = this.keyFor(neighbor);
                if (this.isBlockedMove(neighbor))
                    continue;
                if (this.isOccupied(neighbor, options?.ignoreEntityId) && neighborKey !== goalKey) {
                    continue;
                }
                const tentativeGScore = (gScore.get(currentKey) ?? Infinity) + 1;
                if (tentativeGScore < (gScore.get(neighborKey) ?? Infinity)) {
                    cameFrom.set(neighborKey, currentKey);
                    gScore.set(neighborKey, tentativeGScore);
                    fScore.set(neighborKey, tentativeGScore + this.distanceBetween(neighbor, to));
                    openSet.add(neighborKey);
                }
            }
        }
        return null;
    }
    isInBounds(pos) {
        return pos.x >= 0 && pos.y >= 0 && pos.x < this.width && pos.y < this.height;
    }
    isOccupied(pos, ignoreEntityId) {
        return this.combatData.entityStates.some(e => e.status !== EntityStatus.DEAD &&
            e.entityId !== ignoreEntityId &&
            e.position.x === pos.x &&
            e.position.y === pos.y);
    }
    getNeighbors(pos) {
        const candidates = [
            { x: pos.x + 1, y: pos.y },
            { x: pos.x - 1, y: pos.y },
            { x: pos.x, y: pos.y + 1 },
            { x: pos.x, y: pos.y - 1 },
        ];
        return candidates.filter(candidate => this.isInBounds(candidate));
    }
    keyFor(pos) {
        return `${pos.x},${pos.y}`;
    }
    posFromKey(key) {
        const [x, y] = key.split(',').map(Number);
        return { x, y };
    }
    reconstructPath(cameFrom, currentKey) {
        const path = [this.posFromKey(currentKey)];
        let key = currentKey;
        while (cameFrom.has(key)) {
            key = cameFrom.get(key);
            path.unshift(this.posFromKey(key));
        }
        return path;
    }
}
